import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, X, SlidersHorizontal } from 'lucide-react';
import { API_URL } from '../config';

// Audit events written by the backend -> label shown on screen
const ACTION_LABELS = {
  UPLOAD:                     'Uploaded',
  DOWNLOAD:                   'Downloaded',
  NEW_VERSION:                'New Version',
  SIGNED_VERSION:             'Signed (New Version)',
  SELECTED_FOR_SIGN:          'Selected to Sign',
  SELECTED_FOR_REQUEST:       'Selected for Request',
  SELECTED_FOR_VERIFY:        'Selected to Verify',
  SELECTED_FOR_ARTIFACT_SIGN: 'Selected to Sign Artifact',
  REQUEST_SENT:               'Request Sent',
  VERIFIED:                   'Verified',
  VERIFICATION_FAILED:        'Verification Failed',
  ACCESS_DENIED:              'Access Denied',
  DELETE:                     'Deleted',
};

const ACTION_TYPES = ['All Activities', ...Object.values(ACTION_LABELS)];
const DATE_RANGES  = ['Last 30 Days', 'Last 7 Days', 'Last 90 Days', 'All Time'];
const RANGE_DAYS   = { 'Last 7 Days': 7, 'Last 30 Days': 30, 'Last 90 Days': 90 };

const BLUE   = { bg: '#EFF6FF', color: '#2563EB' };
const GREEN  = { bg: '#F0FDF4', color: '#16A34A' };
const ORANGE = { bg: '#FFF7ED', color: '#EA580C' };
const RED    = { bg: '#FEF2F2', color: '#DC2626' };
const SLATE  = { bg: '#F8FAFC', color: '#475569' };

const ACTION_COLORS = {
  'Uploaded':                  BLUE,
  'New Version':               BLUE,
  'Signed (New Version)':      GREEN,
  'Request Sent':              GREEN,
  'Verified':                  { bg: '#F0FDFA', color: '#0D9488' },
  'Downloaded':                GREEN,
  'Selected to Sign':          ORANGE,
  'Selected for Request':      ORANGE,
  'Selected to Verify':        ORANGE,
  'Selected to Sign Artifact': ORANGE,
  'Verification Failed':       RED,
  'Access Denied':             RED,
  'Deleted':                   RED,
};

const ITEMS_PER_PAGE = 8;

function Dropdown({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', userSelect: 'none' }}>
      <button
        onMouseDown={(e) => { e.preventDefault(); setOpen(o => !o); }}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '8px 14px', border: '1px solid #E2E8F0',
          borderRadius: '8px', background: '#fff',
          fontSize: '14px', color: '#0F172A',
          cursor: 'pointer', fontFamily: "'Inter','Segoe UI',sans-serif",
          minWidth: '155px', outline: 'none',
        }}
      >
        <span style={{ flex: 1, textAlign: 'left' }}>{value}</span>
        <ChevronDown size={14} color="#94A3B8" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 200,
          background: '#fff', border: '1px solid #E2E8F0',
          borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
          minWidth: '175px', overflow: 'hidden',
        }}>
          {options.map((opt) => (
            <div
              key={opt}
              onMouseDown={(e) => { e.preventDefault(); onChange(opt); setOpen(false); }}
              style={{
                padding: '9px 16px', fontSize: '14px', cursor: 'pointer',
                color: opt === value ? '#2563EB' : '#0F172A',
                background: opt === value ? '#EFF6FF' : 'transparent',
                fontWeight: opt === value ? 600 : 400,
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { if (opt !== value) e.currentTarget.style.background = '#F8FAFC'; }}
              onMouseLeave={e => { if (opt !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterPanel({ actionFilter, dateFilter, onActionChange, onDateChange, onClose, onReset }) {
  return (
    <div style={{
      position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 300,
      background: '#fff', border: '1px solid #E2E8F0',
      borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      width: '260px', padding: '20px',
      fontFamily: "'Inter','Segoe UI',sans-serif",
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <span style={{ fontSize: '14px', fontWeight: 700, color: '#0F172A' }}>Filters</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <X size={15} color="#94A3B8" />
        </button>
      </div>

      {/* Action Type filter */}
      <div style={{ marginBottom: '16px' }}>
        <p style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' }}>Action Type</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {ACTION_TYPES.map(opt => (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '5px 6px', borderRadius: '6px', background: actionFilter === opt ? '#EFF6FF' : 'transparent' }}
              onMouseEnter={e => { if (actionFilter !== opt) e.currentTarget.style.background = '#F8FAFC'; }}
              onMouseLeave={e => { if (actionFilter !== opt) e.currentTarget.style.background = 'transparent'; }}
            >
              <input
                type="radio"
                name="actionFilter"
                checked={actionFilter === opt}
                onChange={() => onActionChange(opt)}
                style={{ accentColor: '#2563EB', width: '14px', height: '14px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: actionFilter === opt ? '#2563EB' : '#334155', fontWeight: actionFilter === opt ? 600 : 400 }}>{opt}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Date Range filter */}
      <div style={{ marginBottom: '20px' }}>
        <p style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' }}>Date Range</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {DATE_RANGES.map(opt => (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '5px 6px', borderRadius: '6px', background: dateFilter === opt ? '#EFF6FF' : 'transparent' }}
              onMouseEnter={e => { if (dateFilter !== opt) e.currentTarget.style.background = '#F8FAFC'; }}
              onMouseLeave={e => { if (dateFilter !== opt) e.currentTarget.style.background = 'transparent'; }}
            >
              <input
                type="radio"
                name="dateFilter"
                checked={dateFilter === opt}
                onChange={() => onDateChange(opt)}
                style={{ accentColor: '#2563EB', width: '14px', height: '14px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: dateFilter === opt ? '#2563EB' : '#334155', fontWeight: dateFilter === opt ? 600 : 400 }}>{opt}</span>
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={onReset}
          style={{
            flex: 1, padding: '0 8px', border: '1px solid #E2E8F0',
            borderRadius: '10px', background: '#fff',
            fontSize: '14px', fontWeight: 600, color: '#64748B', cursor: 'pointer',
            height: '44px', fontFamily: "'Inter','Segoe UI',sans-serif",
          }}
        >
          Reset
        </button>
        <button
          onClick={onClose}
          style={{
            flex: 1, padding: '0 8px', border: 'none',
            borderRadius: '10px', background: '#2563EB',
            fontSize: '14px', fontWeight: 600, color: '#fff', cursor: 'pointer',
            height: '44px', fontFamily: "'Inter','Segoe UI',sans-serif",
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#1D4ED8'}
          onMouseLeave={e => e.currentTarget.style.background = '#2563EB'}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

const FONT = "'Inter','Segoe UI',sans-serif";
const DEFAULT_ACTION = 'All Activities';
const DEFAULT_RANGE = 'Last 30 Days';
const DEFAULT_COLORS = { bg: '#F8FAFC', color: '#475569' };
const TABLE_COLUMNS = ['Action', 'User', 'Details', 'IP Address', 'Time'];
const GRID_COLUMNS = '210px 220px 1fr 140px 190px';

/* ------------------------------------------------------------------ */
/* Pure helpers                                                        */
/* ------------------------------------------------------------------ */

const toLogEntry = (r) => ({
  id: r.id,
  action: ACTION_LABELS[r.action] || r.action,
  user: r.user_email || '-',
  details: r.details || '',
  ip: r.ip_address || '-',
  time: r.created_at ? new Date(r.created_at).toLocaleString() : '-',
  ts: r.created_at ? new Date(r.created_at).getTime() : 0,
});

const fetchActivity = async (fileId) => {
  const email = localStorage.getItem('email') || '';
  const res = await fetch(`${API_URL}/api/secure-drive/files/${fileId}/activity?owner_email=${encodeURIComponent(email)}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Could not load activity');
  return data.map(toLogEntry);
};

const filterLogs = (logs, actionFilter, dateFilter) => {
  const days = RANGE_DAYS[dateFilter];
  const cutoff = days ? Date.now() - days * 24 * 60 * 60 * 1000 : 0;
  return logs.filter((log) => (actionFilter === DEFAULT_ACTION || log.action === actionFilter) && log.ts >= cutoff);
};

const activeFilterCount = (actionFilter, dateFilter) =>
  (actionFilter !== DEFAULT_ACTION ? 1 : 0) + (dateFilter !== DEFAULT_RANGE ? 1 : 0);

const emptyTitle = ({ loading, loadError, hasLogs }) => {
  if (loading) return 'Loading activity...';
  if (loadError) return loadError;
  return hasLogs ? 'No entries match the selected filter.' : 'No activity entries yet.';
};

const rangeText = (page, total) => {
  if (total === 0) return 'No items';
  const from = (page - 1) * ITEMS_PER_PAGE + 1;
  const to = Math.min(page * ITEMS_PER_PAGE, total);
  return `Showing ${from} to ${to} of ${total} items`;
};

const filterButtonBg = (showFilter, isFiltered) => (showFilter || isFiltered ? '#EFF6FF' : '#fff');

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */

// Audit entries are written by the system and can't be added or removed here.
function useActivityLogs(fileId) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!fileId) { setLoading(false); return; }
      try {
        setLogs(await fetchActivity(fileId));
      } catch (err) {
        console.error(err);
        setLoadError(err.message || 'Could not load activity');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fileId]);

  return { logs, loading, loadError };
}

/* ------------------------------------------------------------------ */
/* Components                                                          */
/* ------------------------------------------------------------------ */

function FiltersBar({ actionFilter, dateFilter, onAction, onDate, onReset }) {
  const [showFilter, setShowFilter] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isFiltered = actionFilter !== DEFAULT_ACTION || dateFilter !== DEFAULT_RANGE;

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Dropdown value={actionFilter} options={ACTION_TYPES} onChange={onAction} />
        <Dropdown value={dateFilter} options={DATE_RANGES} onChange={onDate} />

        <div ref={filterRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowFilter((o) => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '0 16px',
              height: '44px',
              border: isFiltered ? '1px solid #2563EB' : '1px solid #E2E8F0',
              borderRadius: '10px',
              background: filterButtonBg(showFilter, isFiltered),
              fontSize: '14px',
              color: isFiltered ? '#2563EB' : '#475569',
              fontWeight: 600, cursor: 'pointer',
              fontFamily: FONT,
              transition: 'all 0.15s',
            }}
          >
            <SlidersHorizontal size={14} />
            Filters
            {isFiltered && (
              <span style={{ background: '#2563EB', color: '#fff', borderRadius: '50%', width: '16px', height: '16px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                {activeFilterCount(actionFilter, dateFilter)}
              </span>
            )}
          </button>

          {showFilter && (
            <FilterPanel
              actionFilter={actionFilter}
              dateFilter={dateFilter}
              onActionChange={onAction}
              onDateChange={onDate}
              onClose={() => setShowFilter(false)}
              onReset={onReset}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function LogRow({ log, isLast }) {
  const colors = ACTION_COLORS[log.action] || DEFAULT_COLORS;
  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, padding: '14px 20px', borderBottom: isLast ? 'none' : '1px solid #F1F5F9', alignItems: 'center', background: '#fff', transition: 'background 0.15s' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#F8FAFC'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
    >
      <div>
        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '6px', background: colors.bg, color: colors.color, fontSize: '12px', fontWeight: 700 }}>
          {log.action}
        </span>
      </div>
      <span style={{ fontSize: '14px', color: '#334155' }}>{log.user}</span>
      <span style={{ fontSize: '14px', color: '#64748B' }}>{log.details}</span>
      <span style={{ fontSize: '14px', color: '#64748B', fontFamily: 'monospace' }}>{log.ip}</span>
      <span style={{ fontSize: '14px', color: '#94A3B8' }}>{log.time}</span>
    </div>
  );
}

function EmptyState({ title, hasLogs }) {
  return (
    <div style={{ padding: '60px 20px', textAlign: 'center' }}>
      <p style={{ fontSize: '14px', color: '#94A3B8', margin: '0 0 6px' }}>{title}</p>
      <p style={{ fontSize: '14px', color: '#CBD5E1', margin: 0 }}>
        {hasLogs ? 'Try changing the filter above.' : 'Activity is recorded automatically.'}
      </p>
    </div>
  );
}

function LogsTable({ rows, empty }) {
  return (
    <div style={{ border: '1px solid #E2E8F0', borderRadius: '16px', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, background: '#F8FAFC', padding: '12px 20px', borderBottom: '1px solid #E2E8F0' }}>
        {TABLE_COLUMNS.map((col) => (
          <span key={col} style={{ fontSize: '14px', fontWeight: 700, color: '#0F172A' }}>{col}</span>
        ))}
      </div>

      {rows.length === 0 && <EmptyState title={empty.title} hasLogs={empty.hasLogs} />}
      {rows.map((log, index) => (
        <LogRow key={log.id} log={log} isLast={index === rows.length - 1} />
      ))}
    </div>
  );
}

const PAGE_BTN = { width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #E2E8F0' };

function Pagination({ page, totalPages, total, onPage }) {
  const first = page === 1;
  const last = page === totalPages;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px' }}>
      <span style={{ fontSize: '14px', color: '#64748B' }}>{rangeText(page, total)}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <button
          onClick={() => onPage(Math.max(1, page - 1))}
          disabled={first}
          style={{ ...PAGE_BTN, background: first ? '#F8FAFC' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: first ? 'not-allowed' : 'pointer' }}
        >
          <ChevronLeft size={15} color={first ? '#CBD5E1' : '#475569'} />
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            onClick={() => onPage(p)}
            style={{ ...PAGE_BTN, background: p === page ? '#2563EB' : '#fff', color: p === page ? '#fff' : '#475569', fontSize: '14px', fontWeight: p === page ? 700 : 400, cursor: 'pointer', fontFamily: FONT }}
          >
            {p}
          </button>
        ))}
        <button
          onClick={() => onPage(Math.min(totalPages, page + 1))}
          disabled={last}
          style={{ ...PAGE_BTN, background: last ? '#F8FAFC' : '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: last ? 'not-allowed' : 'pointer' }}
        >
          <ChevronRight size={15} color={last ? '#CBD5E1' : '#fff'} />
        </button>
      </div>
    </div>
  );
}

export default function ActivityLog({ file, onBack }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const fileData = file || state?.file;
  const fileName = fileData?.file_name || fileData?.name || 'No file';

  const { logs, loading, loadError } = useActivityLogs(fileData?.id);
  const [actionFilter, setActionFilter] = useState(DEFAULT_ACTION);
  const [dateFilter, setDateFilter] = useState(DEFAULT_RANGE);
  const [page, setPage] = useState(1);

  const handleActionFilter = (val) => { setActionFilter(val); setPage(1); };
  const handleDateFilter = (val) => { setDateFilter(val); setPage(1); };
  const handleReset = () => { setActionFilter(DEFAULT_ACTION); setDateFilter(DEFAULT_RANGE); setPage(1); };

  const filtered = filterLogs(logs, actionFilter, dateFilter);
  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const hasLogs = logs.length > 0;

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh', padding: '28px', fontFamily: FONT, color: '#0F172A' }}>
      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '17px', fontWeight: 700, color: '#0F172A', margin: '0 0 8px' }}>Activity Log</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#64748B' }}>
          <ArrowLeft size={13} color="#94A3B8" />
          <span onClick={() => navigate(-1)} style={{ color: '#2563EB', fontWeight: 600, cursor: 'pointer' }}>My Drive</span>
          <span>&gt;</span>
          <span style={{ color: '#0F172A', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</span>
        </div>
      </div>

      <FiltersBar
        actionFilter={actionFilter}
        dateFilter={dateFilter}
        onAction={handleActionFilter}
        onDate={handleDateFilter}
        onReset={handleReset}
      />

      <LogsTable rows={paginated} empty={{ title: emptyTitle({ loading, loadError, hasLogs }), hasLogs }} />

      <Pagination page={page} totalPages={totalPages} total={filtered.length} onPage={setPage} />
    </div>
  );
}